import "./index.scss";
import React, {
  FunctionComponent,
  useState,
  ChangeEvent,
  FormEvent,
  useEffect,
} from "react";
import WizardContainer, { WizardProps, SelectedBusiness } from "../../containers/Wizard";
import { CAPTION_PATH } from "../../routing/paths";
import ButtonOptions from "../ButtonOptions";
import LayoutPreview from "./LayoutPreview";
import { isUrlValid } from "../../shared/validations"

export const LayoutStep: FunctionComponent<WizardProps> = ({
  invocations,
  changePath,
  invocate,
  publisher,
}: WizardProps) => {
  const initialCaption = String(invocations["caption"] || "");
  const initialLayout = Number(invocations["layout"] || 1);
  const backgroundUrl = String(invocations["background"] || "");
  const logoUrl = String(invocations["logo"] || "");
  const selectedBiz = invocations["business"] as SelectedBusiness;
  const businessName = String(invocations["business_name"] || selectedBiz?.name || "");

  const [enabledLayouts, setEnabledLayouts] = useState<number[]>([1]);
  const [backgroundInput, setBackgroundInput] = useState<HTMLInputElement | null>();
  const [logoInput, setLogoInput] = useState<HTMLInputElement | null>();
  const [caption, setCaption] = useState<string>(initialCaption);
  const [layout, setLayout] = useState<number>(initialLayout);
  const [layoutIndex, setLayoutIndex] = useState<number>(Math.max(enabledLayouts.indexOf(layout), 0));
  const [hasLogo, setHasLogo] = useState<boolean>(true);

  useEffect(() => setCaption(initialCaption), [setCaption, initialCaption]);
  useEffect(() => setLayout(initialLayout), [setLayout, initialLayout]);
  useEffect(() => setHasLogo(isUrlValid(logoUrl)), [setHasLogo, logoUrl]);
  useEffect(() => setEnabledLayouts(publisher?.enabled_layouts || [1]), [setEnabledLayouts, publisher]);

  const handleSubmit = (path: string) => {
    if (layout !== Number(invocations["layout"])) invocate("layout", `layout-${layout}`);
    changePath(path);
  };
  const onChange = (spellName: string) => (val: ChangeEvent<HTMLInputElement>) => {
    if (val.target.files) invocate(spellName, val.target.files[0]);
  };
  const onInput = (event: FormEvent<HTMLInputElement>) => {
    setCaption(String(event.currentTarget.textContent));
  };
  const previousLayoutIndex = () => {
    if (layoutIndex <= 0) return enabledLayouts.length - 1;
    return layoutIndex - 1;
  };
  const nextLayoutIndex = () => {
    if (layoutIndex >= enabledLayouts.length - 1) return 0;
    return layoutIndex + 1;
  };
  const onToggleButton = () => {
    setHasLogo(!hasLogo);
    invocate("logo", hasLogo ? "" : invocations["default_logo"]);
  }
  const commonProps = {
    logoUrl,
    backgroundUrl,
    logoInput,
    invocate,
    businessName,
    hasLogo,
  };

  return (
    <div>
      <h3>
        Select the <strong>layout</strong> that fits you best.
      </h3>
      <h4>
        Edit your ad&apos;s basic elements and click on the sides to cycle through the
        different templates.
      </h4>
      <div className="preview-stage">
        <LayoutPreview
          onClick={() => {
            const newLayoutIndex = previousLayoutIndex();
            setLayoutIndex(newLayoutIndex)
            setLayout(enabledLayouts[newLayoutIndex]);
          }}
          layout={enabledLayouts[previousLayoutIndex()]}
          layoutClass="teaser-nav previous"
          initialCaption={caption}
          {...commonProps}
        />
        <LayoutPreview
          layout={layout}
          initialCaption={initialCaption}
          {...commonProps}
          onInput={onInput}
          onToggleButton={() => onToggleButton()}
        />
        <LayoutPreview
          onClick={() => {
            const newLayoutIndex = nextLayoutIndex();
            setLayoutIndex(newLayoutIndex)
            setLayout(enabledLayouts[newLayoutIndex]);
          }}
          layout={enabledLayouts[nextLayoutIndex()]}
          layoutClass="teaser-nav next"
          initialCaption={caption}
          {...commonProps}
        />
      </div>
      <button
        type="button"
        className="button replace-background"
        style={{ width: 142, margin: "0 6px", padding: "0 5px" }}
        onClick={() => backgroundInput?.click()}
      >
        <span
          className="mdi mdi-upload"
          style={{ transform: "translateY(1px)", display: "inline-block" }}
        ></span>
        Replace
      </button>
      <input
        type="file"
        id="background-uploader"
        ref={(el) => setBackgroundInput(el)}
        onChange={onChange("background")}
      />
      <input
        type="file"
        id="logo-uploader"
        ref={(el) => setLogoInput(el)}
        onChange={onChange("logo")}
      />
      <ButtonOptions
        changePath={handleSubmit}
        handleSubmit={() => handleSubmit(CAPTION_PATH)}
      />
    </div>
  );
};

export default WizardContainer(LayoutStep);
